import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "./locales/en.json";

export const LANGUAGES = [
  { code: "en", label: "EN", name: "English" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "ja", label: "JP", name: "日本語" },
  { code: "zh", label: "CN", name: "中文" },
] as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    fallbackLng: "en",
    supportedLngs: LANGUAGES.map((l) => l.code),
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "ps_lang",
    },
  });

// Lazy-load non-English locales only when needed (saves ~520KB on first load).
const loaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  de: () => import("./locales/de.json"),
  ja: () => import("./locales/ja.json"),
  zh: () => import("./locales/zh.json"),
};
const loaded = new Set<string>(["en"]);

// Inject Noto Sans CJK stylesheet only when a CJK locale is active. Avoids a
// 184 KB render-chain request for the default (English) load.
// Load only the script-specific Noto Sans family per locale so that
// Japanese kanji are never shaped with Simplified Chinese glyphs (and
// vice versa). Both faces come from the same Noto Sans superfamily,
// so the visual design stays consistent across languages.
const CJK_FONT_HREFS: Record<"ja" | "zh", { id: string; href: string }> = {
  ja: {
    id: "ps-cjk-font-jp",
    href: "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&display=swap",
  },
  zh: {
    id: "ps-cjk-font-sc",
    href: "https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;700&display=swap",
  },
};
function ensureCjkFont(code: string) {
  if (code !== "ja" && code !== "zh") return;
  if (typeof document === "undefined") return;
  const { id, href } = CJK_FONT_HREFS[code as "ja" | "zh"];
  if (document.getElementById(id)) return;
  // Add preconnects for Google Fonts only when the CJK stylesheet is needed.
  const head = document.head;
  const addPreconnect = (href: string, crossorigin?: boolean) => {
    const l = document.createElement("link");
    l.rel = "preconnect";
    l.href = href;
    if (crossorigin) l.crossOrigin = "";
    head.appendChild(l);
  };
  addPreconnect("https://fonts.googleapis.com");
  addPreconnect("https://fonts.gstatic.com", true);
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

function syncHtmlLang(code: string) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = code;
}

async function ensureLocale(lng: string) {
  const code = lng.split("-")[0];
  ensureCjkFont(code);
  syncHtmlLang(code);
  if (loaded.has(code) || !loaders[code]) return;
  const mod = await loaders[code]();
  i18n.addResourceBundle(code, "translation", mod.default, true, true);
  loaded.add(code);
}

i18n.on("languageChanged", (lng) => {
  const code = lng.split("-")[0];
  if (loaded.has(code)) return;
  void ensureLocale(lng).then(() => {
    // Force a re-emit so subscribed components re-render with the new bundle.
    i18n.emit("languageChanged", lng);
  });
});

// On first load, if the detected language isn't EN, load it now.
void ensureLocale(i18n.language || "en");

export default i18n;